import React, { useState } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/providers/AuthProvider';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { KeyRound, ShieldAlert } from 'lucide-react';
import { validatePassword, trimText } from '@/lib/passwordValidation';

export default function ForceChangePassword() {
  const { logout } = useAuth();
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Current password: trim ends (like login). New password: never trimmed — spaces rejected by policy.
    const currentPassword = trimText(form.current_password);
    const newPassword = form.new_password;
    const confirmPassword = form.confirm_password;
    if (newPassword !== confirmPassword) { toast.error('Passwords do not match'); return; }
    const passwordError = validatePassword(newPassword);
    if (passwordError) { toast.error(passwordError); return; }
    setSaving(true);
    try {
      await api.put('/api/auth/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success('Password changed successfully! Please log in again.');
      setTimeout(() => logout(), 1500);
    } catch (e) {
      toast.error(e.message || 'Failed to change password');
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50 p-4" data-testid="force-change-password-page">
      <Card className="max-w-md w-full">
        <CardHeader className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center mb-2">
            <ShieldAlert className="h-7 w-7 text-amber-600" />
          </div>
          <CardTitle>Password Change Required</CardTitle>
          <CardDescription>
            Your password has expired or was reset by an administrator. Please set a new password to continue.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Current / Temporary Password</Label>
              <Input
                data-testid="current-password-input"
                type="password"
                required
                value={form.current_password}
                onChange={e => setForm(f => ({...f, current_password: e.target.value}))}
                placeholder="Enter your current password"
              />
            </div>
            <div className="space-y-2">
              <Label>New Password</Label>
              <Input
                data-testid="new-password-input"
                type="password"
                required
                minLength={8}
                value={form.new_password}
                onChange={e => setForm(f => ({...f, new_password: e.target.value}))}
                placeholder="e.g. Password@123"
              />
              <p className="text-xs text-muted-foreground">Min 8 chars with upper, lower, number &amp; special character. No spaces.</p>
            </div>
            <div className="space-y-2">
              <Label>Confirm New Password</Label>
              <Input
                data-testid="confirm-password-input"
                type="password"
                required
                value={form.confirm_password}
                onChange={e => setForm(f => ({...f, confirm_password: e.target.value}))}
                placeholder="Re-enter new password"
              />
            </div>
            <div className="p-3 rounded-lg bg-slate-50 border text-xs text-muted-foreground">
              <KeyRound className="inline h-3.5 w-3.5 mr-1" />
              You cannot reuse any of your last 4 passwords.
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1" disabled={saving} data-testid="change-password-btn">
                {saving ? 'Changing...' : 'Change Password'}
              </Button>
              <Button type="button" variant="outline" onClick={logout} data-testid="logout-btn">
                Sign Out
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
